import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, ArrowRight, LogIn, UserPlus, CheckCircle2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ThemeToggle from '../components/ThemeToggle';
import BrandMark from '../components/BrandMark';

const benefits = [
 'Full freeze-dry product database',
 'AI tips & recipe assistance',
 'Business + household calculators',
 'Active member community',
 'Curated tutorial library',
 'Optional 1:1 consulting',
];

const LoginPage = () => {
 const navigate = useNavigate();
 const { API, login } = useContext(AuthContext);
 const [loading, setLoading] = useState(false);
 const [authMode, setAuthMode] = useState('login');
 const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });
 const [ageConfirmed, setAgeConfirmed] = useState(false);
 const [termsAccepted, setTermsAccepted] = useState(false);
 // Single combined consent — user ticks once, we submit both legal flags.
 const [combinedConsent, setCombinedConsent] = useState(false);
 // Referral preview — validate a stored personal ref code so the signup
 // card can display "Invited by <name>" (parity with cert-based refs).
 const [refPreview, setRefPreview] = useState(null); // { name, code }
 useEffect(() => {
   setAgeConfirmed(combinedConsent);
   setTermsAccepted(combinedConsent);
 }, [combinedConsent]);

 // Capture ?ref=… on the login page too — users who land here directly
 // (e.g. from a share sheet that stripped query params off / for social
 // previews) still get attribution. FDA/FDACADEMY prefix → certificate,
 // anything else → personal referral code.
 useEffect(() => {
   try {
     const params = new URLSearchParams(window.location.search);
     const ref = (params.get('ref') || '').trim();
     if (!ref) return;
     if (/^(FDA|FDACADEMY)-/i.test(ref)) {
       window.localStorage.setItem('fda_ref_cert', ref.toUpperCase());
     } else {
       window.localStorage.setItem('fda_ref_code', ref.toUpperCase());
     }
   } catch (_) { /* ignore */ }
 }, []);

 // Validate any stored personal referral code so we can render an
 // "Invited by …" banner on the signup card.
 useEffect(() => {
   let alive = true;
   (async () => {
     try {
       const code = (window.localStorage.getItem('fda_ref_code') || '').trim().toUpperCase();
       if (!code) return;
       const { data } = await axios.get(`${API}/referrals/validate/${encodeURIComponent(code)}`);
       if (alive && data?.valid) setRefPreview({ name: data.referrer_name, code });
     } catch (_) { /* ignore */ }
   })();
   return () => { alive = false; };
 }, [API]);

 const handleAuth = async (e) => {
 e.preventDefault();
 if (authMode === 'register' && !combinedConsent) {
 toast.error('Please tick the box to confirm you are 18+ and accept the Terms of Use.');
 return;
 }
 setLoading(true);
 try {
 if (authMode === 'register') {
 if (formData.password !== formData.confirmPassword) {
 toast.error('Passwords do not match'); return;
 }
 if (formData.password.length < 6) {
 toast.error('Password must be at least 6 characters'); return;
 }
 }
 const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
 const payload = { email: formData.email, password: formData.password };
 if (authMode === 'register') {
   payload.age_confirmed = true;
   payload.accepted_terms = true;
   payload.accepted_terms_version = '2026-02-canada-v1';
   // Referral certificate captured on the landing page (?ref=FDA-…) is
   // still passed through — access/influencer codes have moved to the
   // Pricing page so new users always see the payment gateway first.
   try {
     const stored = window.localStorage.getItem('fda_ref_cert');
     if (stored && /^(FDA|FDACADEMY)-/i.test(stored)) payload.referral_cert = stored.toUpperCase();
     const storedCode = window.localStorage.getItem('fda_ref_code');
     if (storedCode) payload.referral_code = storedCode.toUpperCase();
   } catch (_) { /* ignore */ }
 }
 const res = await axios.post(`${API}${endpoint}`, payload);
 if (res.data.access_token) {
 if (authMode === 'register') {
   try {
     window.localStorage.removeItem('fda_ref_cert');
     window.localStorage.removeItem('fda_ref_code');
   } catch (_) { /* ignore */ }
 }
 login(res.data.access_token, res.data.user);
 toast.success(`Welcome${authMode === 'login' ? ' back' : ' to Freeze Dry Academy'}!`);
 // Route new signups through the Pricing gateway so they can pick a
 // plan (free / access code / influencer code / paid) before landing
 // inside the app. Returning members go straight to their dashboard.
 navigate(authMode === 'register' ? '/freedriq?tier=free&phase=pre&from=signup' : '/dashboard');
 }
 } catch (error) {
 toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Authentication failed');
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="min-h-screen flex flex-col bg-background" data-testid="login-page">
 <header className="border-b border-border bg-background sticky top-0 z-30">
 <div className="container mx-auto px-6 h-16 flex items-center justify-between">
 <Button variant="ghost" onClick={() => navigate('/')} className="-ml-3 font-semibold" data-testid="login-back-btn">
 <ArrowLeft className="w-4 h-4 mr-2" /> Back home
 </Button>
 <BrandMark size="md" onClick={() => navigate('/')} />
 <ThemeToggle />
 </div>
 </header>

 <main className="flex-1 grid lg:grid-cols-2 gap-0 max-w-7xl w-full mx-auto">
 {/* Left — navy promo panel */}
 <div className="hidden lg:flex bg-navy-spotlight text-background p-12 xl:p-16 items-center">
 <div className="max-w-md animate-fade-up">
 <span className="pill-badge"><ShieldCheck className="w-3.5 h-3.5" /> Members</span>
 <h2 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tight mt-6">
 The complete
 <br /><span className="text-amber-brand">freeze-dry hub.</span>
 </h2>
 <p className="mt-5 text-background/70 leading-relaxed">
 Lessons, calculators, recipes, and a community — built for home operators and small businesses.
 </p>
 <ul className="mt-8 space-y-2.5">
 {benefits.map(b => (
 <li key={b} className="flex items-start gap-3 text-background/90 text-[15px]">
 <CheckCircle2 className="w-4.5 h-4.5 text-primary mt-0.5 flex-shrink-0" />
 {b}
 </li>
 ))}
 </ul>
 <div className="mt-10 flex items-center gap-3 text-xs text-background/60">
 <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
 <span>Practical, no fluff. Built for operators.</span>
 </div>
 </div>
 </div>

 {/* Right — form */}
 <div className="flex items-center justify-center px-6 py-12 lg:py-20">
 <div className="w-full max-w-md">
 <span className="eyebrow" data-testid="login-eyebrow">
 {authMode === 'login' ? 'Welcome back' : 'Become a member'}
 </span>
 <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 text-balance">
 {authMode === 'login' ? 'Sign in to continue.' : 'Create your account.'}
 </h1>
 <p className="mt-3 text-muted-foreground">
 {authMode === 'login'
 ? 'Access your batches, calculators, and lessons.'
 : 'Free to start — upgrade to Pro anytime.'}
 </p>

 <Tabs value={authMode} onValueChange={setAuthMode} className="mt-7">
 <TabsList className="grid grid-cols-2 w-full" data-testid="login-tabs">
 <TabsTrigger value="login" data-testid="login-tab">
 <LogIn className="w-3.5 h-3.5 mr-2" /> Sign in
 </TabsTrigger>
 <TabsTrigger value="register" data-testid="register-tab">
 <UserPlus className="w-3.5 h-3.5 mr-2" /> Sign up
 </TabsTrigger>
 </TabsList>

 <form onSubmit={handleAuth} className="mt-6 space-y-4">
{authMode === 'register' && refPreview && (
  <div
    className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm flex items-start gap-2"
    data-testid="ref-preview-banner"
  >
    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
    <div className="leading-snug">
      <div className="font-bold text-foreground">Invited by {refPreview.name}</div>
      <div className="text-muted-foreground text-xs mt-0.5">
        Code <span className="font-mono font-bold">{refPreview.code}</span> will be applied when you finish signing up.
      </div>
    </div>
  </div>
)}
 <div className="space-y-1.5">
 <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
 <Input
 id="email" type="email" required
 value={formData.email}
 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
 placeholder="you@example.com"
 className="h-11"
 data-testid="email-input"
 />
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
 <Input
 id="password" type="password" required
 value={formData.password}
 onChange={(e) => setFormData({ ...formData, password: e.target.value })}
 placeholder={authMode === 'register' ? 'At least 6 characters' : '••••••••'}
 className="h-11"
 data-testid="password-input"
 />
 {authMode === 'login' && (
 <div className="flex justify-end pt-0.5">
 <Link
 to="/forgot-password"
 className="text-xs font-semibold text-primary hover:underline"
 data-testid="forgot-password-link"
 >
 Forgot password?
 </Link>
 </div>
 )}
 </div>
 {authMode === 'register' && (
 <div className="space-y-1.5">
 <Label htmlFor="confirmPassword" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirm Password</Label>
 <Input
 id="confirmPassword" type="password" required
 value={formData.confirmPassword}
 onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
 placeholder="Repeat your password"
 className="h-11"
 data-testid="confirm-password-input"
 />
 </div>
 )}
 {authMode === 'register' && (
<label className="relative z-10 flex items-start gap-2.5 text-xs cursor-pointer pt-1 rounded-lg border border-border p-3 bg-background hover:border-primary/60 transition-colors" data-testid="combined-consent-label">
<input
type="checkbox"
checked={combinedConsent}
onChange={(e) => setCombinedConsent(e.target.checked)}
className="mt-0.5 h-4 w-4 rounded border-input flex-shrink-0"
data-testid="combined-consent-checkbox"
required
/>
<span className="text-muted-foreground leading-relaxed">
I confirm I am <strong className="text-foreground">18 or older</strong> and I agree to the{' '}
<Link to="/terms" target="_blank" rel="noopener" className="underline font-bold text-foreground">Terms of Use</Link>
{' '}— including the <strong className="text-foreground">anti-piracy prosecution</strong> and <strong className="text-foreground">Canadian jurisdiction</strong> clauses.
</span>
</label>
)}

 <Button
 type="submit" disabled={loading} size="lg"
 className="w-full h-12 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90 mt-2"
 data-testid="auth-submit-btn"
 >
 {loading ? 'Please wait…' : (
 <>
 {authMode === 'login' ? 'Sign in' : 'Create account'}
 <ArrowRight className="ml-2 w-4 h-4" />
 </>
 )}
 </Button>
 </form>
 </Tabs>
 </div>
 </div>
 </main>

 <footer className="border-t border-border">
 <div className="container mx-auto px-6 py-6 text-center">
 <p className="text-xs text-muted-foreground">
 Practical tools and learning for real freeze-dry operators.
 </p>
 </div>
 </footer>
 </div>
 );
};

export default LoginPage;
